/* ====================================================================
   BRIGHTSPAN · MOTION
   Vanilla. Respects prefers-reduced-motion. No animation of layout.
   CSS is linked from index.html for synchronous load (no FOUC).
   ==================================================================== */

(() => {
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isFinePointer  = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ------------------------------------------------------------------
     OVERDRIVE · Spring physics core
     A semi-implicit Euler spring solver. All presets are critically
     damped or slightly overdamped, so motion settles without bouncing.
     ------------------------------------------------------------------ */
  const SPRINGS = {
    soft:    { stiffness: 160, damping: 28, mass: 1.0 },
    default: { stiffness: 200, damping: 30, mass: 1.0 },
    snappy:  { stiffness: 280, damping: 34, mass: 1.0 },
    liquid:  { stiffness: 110, damping: 23, mass: 1.2 },
    track:   { stiffness: 230, damping: 32, mass: 1.0 }
  };

  /**
   * Creates a stepping spring. Call the returned function each frame
   * to advance the simulation by dt. Returns { x, v, done }.
   */
  const makeSpring = ({ from = 0, to = 0, velocity = 0, preset = SPRINGS.default }) => {
    let x = from, v = velocity;
    const { stiffness, damping, mass } = preset;
    return (target = to, dt = 1 / 60) => {
      const Fspring = -stiffness * (x - target);
      const Fdamp   = -damping * v;
      const a       = (Fspring + Fdamp) / mass;
      v += a * dt;
      x += v * dt;
      const done = Math.abs(x - target) < 0.01 && Math.abs(v) < 0.05;
      return { x, v, done };
    };
  };

  /**
   * One-shot spring tween from `from` → `to`. Calls `onUpdate(x)` each
   * frame. Calls `onDone()` when settled. Returns cancel handle.
   */
  const springTween = ({ from, to, velocity = 0, preset = SPRINGS.default, onUpdate, onDone }) => {
    if (prefersReduced) {
      onUpdate?.(to);
      onDone?.();
      return () => {};
    }
    const step = makeSpring({ from, to, velocity, preset });
    let last = performance.now();
    let raf;
    let cancelled = false;
    const tick = (now) => {
      if (cancelled) return;
      const dt = Math.min(1 / 30, (now - last) / 1000);
      last = now;
      const { x, done } = step(to, dt);
      onUpdate?.(x);
      if (done) { onDone?.(); return; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  };

  /* ------------------------------------------------------------------
     0 · Theme toggle with liquid morph reveal
     ------------------------------------------------------------------ */
  const themeToggle = document.getElementById('theme-toggle');
  const themeMeta   = document.querySelector('meta[name="theme-color"]');

  // Snapshot colours so the morph overlay can show the INCOMING theme
  const THEME_BG = {
    dark:  'oklch(0.16 0.012 60)',
    light: 'oklch(0.97 0.014 85)'
  };
  const THEME_META = {
    dark:  '#1a140f',
    light: '#f5ecde'
  };

  let morphInFlight = false;
  const runThemeMorph = (incoming) => {
    if (prefersReduced || morphInFlight) return false;
    morphInFlight = true;

    const btn = themeToggle.getBoundingClientRect();
    const cx  = btn.left + btn.width / 2;
    const cy  = btn.top + btn.height / 2;
    const dx  = Math.max(cx, window.innerWidth  - cx);
    const dy  = Math.max(cy, window.innerHeight - cy);
    const rMax = Math.hypot(dx, dy) + 24;

    const overlay = document.createElement('div');
    overlay.className = 'theme-morph';
    overlay.style.cssText = `
      position: fixed; inset: 0; z-index: 9999;
      pointer-events: none;
      background: ${THEME_BG[incoming]};
      clip-path: circle(0px at ${cx}px ${cy}px);
      -webkit-clip-path: circle(0px at ${cx}px ${cy}px);
      will-change: clip-path;
    `;
    document.body.appendChild(overlay);

    springTween({
      from: 0,
      to: rMax,
      preset: SPRINGS.liquid,
      onUpdate: (r) => {
        overlay.style.clipPath = `circle(${r}px at ${cx}px ${cy}px)`;
        overlay.style.webkitClipPath = `circle(${r}px at ${cx}px ${cy}px)`;
      },
      onDone: () => {
        // Swap theme on root, then fade out the overlay
        applyTheme(incoming);
        overlay.style.transition = 'opacity 0.35s cubic-bezier(0.25, 1, 0.5, 1)';
        overlay.style.opacity = '0';
        setTimeout(() => {
          overlay.remove();
          morphInFlight = false;
        }, 360);
      }
    });
    return true;
  };

  const applyTheme = (theme) => {
    if (theme === "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    if (themeMeta) themeMeta.setAttribute('content', THEME_META[theme]);
    try { localStorage.setItem('bs-theme', theme); } catch (e) {}
    if (themeToggle) themeToggle.setAttribute('aria-pressed', theme === 'light' ? 'true' : 'false');
  };

  const setTheme = (theme, withMorph = false) => {
    if (withMorph && runThemeMorph(theme)) return;
    applyTheme(theme);
  };

  if (themeToggle) {
    const initial = document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
    themeToggle.setAttribute('aria-pressed', initial === 'light' ? 'true' : 'false');
    themeToggle.addEventListener('click', () => {
      if (morphInFlight) return;
      const isLight = document.documentElement.getAttribute("data-theme") !== "dark";
      setTheme(isLight ? 'dark' : 'light', true);
    });
  }

  /* ------------------------------------------------------------------
     1 · Hero video — force autoplay if the browser holds it back
     ------------------------------------------------------------------ */
  const heroVideo = document.querySelector('.hero__video');
  if (heroVideo && !prefersReduced) {
    const kick = () => heroVideo.play().catch(() => {});
    kick();
    heroVideo.addEventListener('canplay', kick, { once: true });
    window.addEventListener('load', kick, { once: true });
  }

  /* ------------------------------------------------------------------
     2 · Reveal-up via IntersectionObserver (delay attr supported)
     ------------------------------------------------------------------ */
  const reveals = document.querySelectorAll('.reveal-up');
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = parseFloat(entry.target.dataset.delay || 0);
          entry.target.style.transitionDelay = `${delay}s`;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -80px 0px' });
    reveals.forEach((el) => io.observe(el));
  } else {
    reveals.forEach((el) => el.classList.add('is-in'));
  }

  /* ------------------------------------------------------------------
     4 · Nav stuck state
     ------------------------------------------------------------------ */
  const nav = document.getElementById('nav');
  if (nav) {
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 24 && !nav.classList.contains('is-stuck')) nav.classList.add('is-stuck');
      else if (y <= 24 && nav.classList.contains('is-stuck')) nav.classList.remove('is-stuck');
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------
     5 · Manifesto: split into words, then activate line-by-line on scroll
     ------------------------------------------------------------------ */
  const manifestoLines = document.querySelectorAll('.manifesto__line');

  // Pre-split each line into per-word spans so words can stagger in
  // without losing italic/ember spans that already wrap inside the line.
  const splitLineIntoWords = (lineEl) => {
    if (prefersReduced) return;
    const walker = document.createTreeWalker(lineEl, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n; while ((n = walker.nextNode())) textNodes.push(n);
    let i = 0;
    textNodes.forEach((textNode) => {
      const parent = textNode.parentNode;
      const parts = textNode.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach((part) => {
        if (part === '') return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
        } else {
          const wrap = document.createElement('span');
          wrap.className = 'm-word';
          const inner = document.createElement('span');
          inner.textContent = part;
          inner.style.transitionDelay = `${i * 60}ms`;
          wrap.appendChild(inner);
          frag.appendChild(wrap);
          i++;
        }
      });
      parent.replaceChild(frag, textNode);
    });
  };
  manifestoLines.forEach(splitLineIntoWords);

  if (manifestoLines.length && 'IntersectionObserver' in window) {
    const ioM = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-active');
      });
    }, { threshold: 0.55 });
    manifestoLines.forEach((l) => ioM.observe(l));
  } else {
    manifestoLines.forEach((l) => l.classList.add('is-active'));
  }

  /* ------------------------------------------------------------------
     5b · Footer reveal stagger
     ------------------------------------------------------------------ */
  const footerEl = document.querySelector('.footer');
  if (footerEl && 'IntersectionObserver' in window) {
    const ioF = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          ioF.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    ioF.observe(footerEl);
  }

  /* ------------------------------------------------------------------
     5c · Pause the infinite ticker animation when off-screen (CPU win)
     ------------------------------------------------------------------ */
  const animationPauseTargets = document.querySelectorAll('.ticker');
  if (animationPauseTargets.length && 'IntersectionObserver' in window) {
    const ioPause = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-visible', entry.isIntersecting);
      });
    }, { threshold: 0 });
    animationPauseTargets.forEach((el) => ioPause.observe(el));
  } else {
    // No IntersectionObserver support: keep them running so the design is intact.
    animationPauseTargets.forEach((el) => el.classList.add('is-visible'));
  }

  /* ------------------------------------------------------------------
     6 · Service blocks: in-view trigger for staggered log
     ------------------------------------------------------------------ */
  const services = document.querySelectorAll('.service');
  if (services.length && 'IntersectionObserver' in window) {
    const ioS = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          ioS.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    services.forEach((s) => ioS.observe(s));
  } else {
    services.forEach((s) => s.classList.add('in-view'));
  }

  /* ------------------------------------------------------------------
     7 · Method steps: highlight active as user scrolls
     ------------------------------------------------------------------ */
  const methodSteps = document.querySelectorAll('.method__step');
  if (methodSteps.length && 'IntersectionObserver' in window) {
    const ioMS = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('is-active');
      });
    }, { threshold: 0.5 });
    methodSteps.forEach((s) => ioMS.observe(s));
  }

  /* ------------------------------------------------------------------
     8 · Year
     ------------------------------------------------------------------ */
  const yearEl = document.getElementById('year');
  if (yearEl) yearEl.textContent = new Date().getFullYear();

  /* ------------------------------------------------------------------
     9 · Smooth-scroll (lite). Lerp the document scroll position.
     ------------------------------------------------------------------ */
  if (!prefersReduced && isFinePointer && 'requestAnimationFrame' in window) {
    let target = window.scrollY;
    let current = window.scrollY;
    let rafId = null;
    let active = false;

    const lerp = (a, b, n) => a + (b - a) * n;

    // Let a scrollable descendant (e.g. the code viz's overflow:auto panel)
    // handle its own wheel input instead of the page hijacking it.
    const scrollsInternally = (el, deltaY) => {
      for (let node = el; node && node !== document.body; node = node.parentElement) {
        const style = getComputedStyle(node);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
          const atTop = node.scrollTop <= 0;
          const atBottom = node.scrollTop + node.clientHeight >= node.scrollHeight;
          if (!((deltaY < 0 && atTop) || (deltaY > 0 && atBottom))) return true;
        }
      }
      return false;
    };

    const onWheel = (e) => {
      // Let modifier keys and form scroll behave natively
      if (e.ctrlKey || e.metaKey) return;
      if (scrollsInternally(e.target, e.deltaY)) return;
      e.preventDefault();
      target = Math.max(0, Math.min(document.documentElement.scrollHeight - window.innerHeight, target + e.deltaY));
      if (!active) {
        active = true;
        loop();
      }
    };

    const loop = () => {
      current = lerp(current, target, 0.11);
      window.scrollTo(0, current);
      if (Math.abs(target - current) > 0.5) {
        rafId = requestAnimationFrame(loop);
      } else {
        active = false;
        cancelAnimationFrame(rafId);
      }
    };

    // Re-sync if user uses keyboard / touch / native scroll
    window.addEventListener('scroll', () => {
      if (!active) { target = window.scrollY; current = window.scrollY; }
    }, { passive: true });

    window.addEventListener('wheel', onWheel, { passive: false });
  }

  /* ------------------------------------------------------------------
     10 · Custom cursor · continuous spring tracking
     ------------------------------------------------------------------ */
  if (isFinePointer && !prefersReduced) {
    const cursor = document.getElementById('cursor');
    if (cursor) {
      let tx = -100, ty = -100;
      const stepX = makeSpring({ from: -100, preset: SPRINGS.snappy });
      const stepY = makeSpring({ from: -100, preset: SPRINGS.snappy });
      let last = performance.now();
      let raf = null;

      const tick = (now) => {
        const dt = Math.min(1 / 30, (now - last) / 1000);
        last = now;
        const { x: cx, done: dx } = stepX(tx, dt);
        const { x: cy, done: dy } = stepY(ty, dt);
        cursor.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
        if (!dx || !dy) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = null;
        }
      };
      window.addEventListener('pointermove', (e) => {
        tx = e.clientX; ty = e.clientY;
        if (!cursor.classList.contains('is-visible')) cursor.classList.add('is-visible');
        if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      });

      const linkSelector = 'a, button, .cta, .nav__cta, .service__bullets li, .method__step';
      document.querySelectorAll(linkSelector).forEach((el) => {
        el.addEventListener('pointerenter', () => cursor.classList.add('is-link'));
        el.addEventListener('pointerleave', () => cursor.classList.remove('is-link'));
      });

      window.addEventListener('pointerleave', () => cursor.classList.remove('is-visible'));
    }
  }

  /* ------------------------------------------------------------------
     11 · Partner testimonials carousel
     ------------------------------------------------------------------ */
  const carousel = document.querySelector('.partners');
  if (carousel) {
    const viewport = carousel.querySelector('.partners__viewport');
    const track    = carousel.querySelector('.partners__track');
    const slides   = carousel.querySelectorAll('.partners__slide');
    const prevBtn  = carousel.querySelector('.partners__btn--prev');
    const nextBtn  = carousel.querySelector('.partners__btn--next');
    const counter  = carousel.querySelector('.partners__counter');
    const total    = slides.length;
    let index = 0;

    // Position is now in pixels so we can drive it with spring physics
    let trackPx = 0;
    let viewportWidth = viewport.clientWidth || 1;
    let cancelSettle = null;
    let dragVelocityPxPerSec = 0;

    const pad2 = (n) => String(n).padStart(2, '0');
    const targetPxFor = (i) => -i * viewportWidth;
    const setTrack = (px) => {
      trackPx = px;
      track.style.transform = `translate3d(${px}px, 0, 0)`;
    };

    const updateMeta = () => {
      if (counter) counter.textContent = `${pad2(index + 1)} / ${pad2(total)}`;
      slides.forEach((s, i) => {
        s.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
        s.inert = i !== index;
      });
    };

    const springToIndex = (velocity = 0) => {
      if (cancelSettle) cancelSettle();
      const target = targetPxFor(index);
      if (prefersReduced) { setTrack(target); return; }
      cancelSettle = springTween({
        from: trackPx,
        to: target,
        velocity,
        preset: SPRINGS.track,
        onUpdate: setTrack,
        onDone: () => { setTrack(target); cancelSettle = null; }
      });
    };

    const setIdx = (n, velocity = 0) => {
      index = ((n % total) + total) % total;
      updateMeta();
      springToIndex(velocity);
    };

    // Re-measure on resize and clamp
    window.addEventListener('resize', () => {
      viewportWidth = viewport.clientWidth || 1;
      setTrack(targetPxFor(index));
    });

    prevBtn?.addEventListener('click', () => setIdx(index - 1));
    nextBtn?.addEventListener('click', () => setIdx(index + 1));

    viewport.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); setIdx(index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); setIdx(index + 1); }
    });

    // Pointer drag with velocity tracking
    let dragStartX = 0;
    let dragLastX  = 0;
    let dragLastT  = 0;
    let dragging   = false;

    const onDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      dragging = true;
      if (cancelSettle) { cancelSettle(); cancelSettle = null; }
      viewportWidth = viewport.clientWidth || 1;
      dragStartX = e.clientX;
      dragLastX  = e.clientX;
      dragLastT  = performance.now();
      dragVelocityPxPerSec = 0;
      viewport.classList.add('is-grabbing');
      viewport.setPointerCapture?.(e.pointerId);
    };
    const onMove = (e) => {
      if (!dragging) return;
      const now = performance.now();
      const dt  = Math.max(1, now - dragLastT);
      const dx  = e.clientX - dragLastX;
      dragVelocityPxPerSec = (dx / dt) * 1000;
      dragLastX = e.clientX;
      dragLastT = now;

      let bias = e.clientX - dragStartX;
      // Rubber-band resistance at endpoints
      if (index === 0 && bias > 0) bias *= 0.45;
      else if (index === total - 1 && bias < 0) bias *= 0.45;

      setTrack(targetPxFor(index) + bias);
    };
    const onUp = (e) => {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-grabbing');

      const dragDX = e.clientX - dragStartX;
      const threshold = Math.max(50, viewportWidth * 0.12);
      // Either a fast flick or a past-threshold drag advances
      const flick = Math.abs(dragVelocityPxPerSec) > 600;
      const past  = Math.abs(dragDX) > threshold;

      if (flick || past) {
        const dir = (Math.abs(dragVelocityPxPerSec) > 600 ? dragVelocityPxPerSec : dragDX) < 0 ? 1 : -1;
        setIdx(index + dir, dragVelocityPxPerSec);
      } else {
        springToIndex(dragVelocityPxPerSec * 0.3);
      }
    };

    viewport.addEventListener('pointerdown', onDown);
    viewport.addEventListener('pointermove', onMove);
    viewport.addEventListener('pointerup', onUp);
    viewport.addEventListener('pointercancel', onUp);

    setTrack(targetPxFor(0));
    updateMeta();
  }

  /* ------------------------------------------------------------------
     12 · Anchor smooth-jump (works alongside lerp scroll)
     ------------------------------------------------------------------ */
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const top  = rect.top + window.scrollY - 64;
      window.scrollTo({ top, behavior: prefersReduced ? 'auto' : 'smooth' });
    });
  });


  /* ------------------------------------------------------------------
     13 · Mobile menu · native <dialog> (focus trap + Esc for free)
     ------------------------------------------------------------------ */
  (() => {
    const burger = document.getElementById('nav-burger');
    const menu   = document.getElementById('navmenu');
    const closeBtn = document.getElementById('navmenu-close');
    if (!burger || !menu || typeof menu.showModal !== 'function') return;

    const open = () => { menu.showModal(); burger.setAttribute('aria-expanded', 'true'); };
    const shut = () => { if (menu.open) menu.close(); };

    burger.addEventListener('click', open);
    closeBtn?.addEventListener('click', shut);
    menu.addEventListener('close', () => burger.setAttribute('aria-expanded', 'false'));
    // Backdrop click closes
    menu.addEventListener('click', (e) => { if (e.target === menu) shut(); });
    // Choosing a destination closes the sheet (smooth-scroll handler still runs)
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', shut));
    // Never leave it open after resizing up to desktop
    window.addEventListener('resize', () => { if (window.innerWidth > 720) shut(); });
  })();

  /* ------------------------------------------------------------------
     14 · Developer console signature (delight for the curious)
     ------------------------------------------------------------------ */
  try {
    const ember = 'color:#d68940;font-family:Instrument Serif,serif;font-size:22px;font-style:italic;';
    const dim   = 'color:#8a7c6d;font-family:JetBrains Mono,monospace;font-size:11px;letter-spacing:0.08em;';
    console.log('%cBrightspan.', ember);
    console.log('%cYou are reading the source. Good. Most do not.', dim);
    console.log('%cAI research, shipped to the floor. tejobirwork@gmail.com', dim);
  } catch (e) { /* console unavailable */ }
})();
